import createGenerateClassName from '@mui/styles/createGenerateClassName';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import ErrorBoundary from '@innovyze/stylovyze/lib/components/ErrorBoundary';
import LoadingPage from '@innovyze/stylovyze/lib/components/LoadingPage';
import Notifier from '@innovyze/stylovyze/lib/components/Notifier';
import { TermsAndConditionsWrapper } from '@innovyze/stylovyze/lib/components/TermsAndConditionsDialog';
import Pages from '@innovyze/stylovyze/lib/hocs/Pages';
import { selectIsAppLoading, useSelectNamespace, useSelectUserContext, } from '@innovyze/stylovyze/lib/selectors';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import NotificationHubProvider from '../../contexts/NotificationHubProvider';
import useInferTenantId from '../../storages/useInferTenantId';
import { RegisteredApplications, } from '@innovyze/stylovyze/lib/types/application.types';
import { Environments, getEnvironment, } from '@innovyze/stylovyze/lib/utils/environment';
import { GlobalizationProvider, LoadingProvider, SiteConfigProvider, } from '../../contexts';
import { App, GlobalStyle, StyledAppBody } from './ApplicationWrapper.styles';
import { setApplicationContext } from '@innovyze/stylovyze/lib/actions';
import ExpiredTenantDialogWrapper from '@innovyze/stylovyze/lib/components/ExpiredTenantDialog';
import { LocalizationProviderLuxon } from '@innovyze/stylovyze/lib/components/MuiExposes/MuiLocalizationProviders';
import AuthenticationRouter from '@innovyze/stylovyze/lib/contexts/AuthenticationWrapper/AuthenticationRouter';
import { FeatureFlagProvider } from '@innovyze/stylovyze/lib/contexts/FeatureFlag';
import { S3DataAuthProvider } from '@innovyze/stylovyze/lib/contexts/S3DataAuth';
import SettingsProvider from '@innovyze/stylovyze/lib/contexts/Settings';
import MapProvider from '@innovyze/stylovyze/lib/hocs/MapProvider/MapProvider';
import { useIsCypress } from '@innovyze/stylovyze/lib/utils/isCypress';
import { innovyzeTheme } from '@innovyze/stylovyze/lib/utils/styles';
// ApplicationWrapper sub-components
import NotificationManagement from './Components/Header/StylovyzeNotification/NotificationManagement';
import { WhatsNewDetailIFrame } from './Components/Header/WhatsNewDetaiIIFrame';
import { WhatsNewPage } from './Components/Header/WhatsNewPage';
import LocalStorageStatesSynchronizer from './Components/LocalStorageStatesSynchronizer';
import ModalsForUserHelp from './Components/ModalsForUserHelp/ModalsForUserHelp';
import NavigateToProvider from './Components/NavigateToProvider';
import InnoStylesProvider from './theme/InnoStylesProvider';
import InnoWeaveThemeConnector from './theme/InnoWeaveThemeConnector';
import AboutToExpireFeature from './features/AboutToExpire/AboutToExpireFeature';
import ChooseHeader from './Components/ChooseHeader';
import { MoveableChatbot } from '../../components/Chatbot/';
import { LogoutContextProvider } from '../../contexts/AuthenticationWrapper/LogoutContext';
import { AnalyticsProviderWrapper } from '../../contexts/AnalyticsProvider/AnalyticsProviderWrapper';
function isAuthorized(userContext) {
    return userContext?.defaultSite && userContext?.innovyzeRole;
}
function useInitDataDog(registeredApplication) {
    const isCypress = useIsCypress();
    const isLocalHost = getEnvironment() === Environments.LOCAL;
    try {
        if (!isCypress && !isLocalHost) {
            datadogLogs.init({
                clientToken: 'pub7d6a245f499f54cf462364ef11e9693b',
                site: 'datadoghq.com',
                forwardErrorsToLogs: true,
                sampleRate: 100,
                service: registeredApplication,
                env: getEnvironment(),
                silentMultipleInit: true,
            });
            datadogRum.init({
                applicationId: '4e5790ce-490f-4b97-bc3a-c04ed76376e3',
                clientToken: 'pub72ab70678e164bc555df8c813465485b',
                site: 'datadoghq.com',
                service: registeredApplication,
                env: getEnvironment(),
                // Specify a version number to identify the deployed version of your application in Datadog
                // version: '1.0.0',
                sampleRate: 100,
                trackInteractions: true,
                silentMultipleInit: true,
                // Add additional tracing origins. Each service needs allow-listed x-dd headers.
                // Check in saas-idp-router repo for examples.
                allowedTracingOrigins: [
                    /https:\/\/saas-idp-router.info360.*.com/,
                ],
            });
        }
    }
    catch (e) {
        console.error('DataDog Init Error', e);
    }
}
/**
 * A wrapper with the entire frontend setup for our modules. In addition to applying global styles and including the Header,
 * Nav, and Page portions of your application, it also wraps it with the site-config, Auth-0, and
 * Notistack providers.
 */
export const ApplicationWrapper = ({ globalization, configUrl, config, pages, authenticate, theme = innovyzeTheme, showSideNav = true, branding, isTenantAware = true, helpUrl, aboutBox, validateTerms, applicationContext, expiredSubscriptionsSet, loginPicker, injectTenantId = false, helpModals = false, hasWeaveTheme = false, withHelpbot = false, disableMapProvider = false, notificationHub, hasSharedWorkers = false, }) => {
    useInitDataDog(applicationContext?.applicationName ??
        RegisteredApplications.UNREGISTERED);
    let authorizedPages = [];
    const dispatch = useDispatch();
    const userContext = useSelectUserContext();
    const appName = useSelectNamespace();
    const isLoading = selectIsAppLoading();
    const { inEmulation } = userContext;
    if (isLoading) {
        authorizedPages = [
            {
                to: '*',
                render: React.createElement(LoadingPage, null),
            },
        ];
    }
    else if (isAuthorized(userContext)) {
        authorizedPages = [
            ...pages,
            {
                to: '/whats-new',
                render: React.createElement(WhatsNewPage, null),
            },
            {
                to: '/whats-new/detail',
                render: React.createElement(WhatsNewDetailIFrame, null),
            },
            {
                to: '/notification-management',
                render: React.createElement(NotificationManagement, null),
            },
        ];
    }
    else {
        authorizedPages = [
            {
                to: '*',
                render: (React.createElement("div", null,
                    React.createElement("h1", null, "Not Authorized"),
                    React.createElement("p", null, "Contact your company admin"))),
            },
        ];
    }
    const generateClassName = createGenerateClassName({
        productionPrefix: 'inno',
    });
    const notificationConfig = !!notificationHub?.applicationId &&
        !!notificationHub?.tenantId &&
        !!notificationHub?.userId &&
        !!notificationHub?.product
        ? {
            applicationId: notificationHub.applicationId,
            tenantId: notificationHub.tenantId,
            userId: notificationHub.userId,
            product: notificationHub.product,
        }
        : undefined;
    // Effects
    useInferTenantId(injectTenantId);
    React.useEffect(() => {
        if (applicationContext && !appName) {
            dispatch(setApplicationContext(applicationContext));
        }
    }, [applicationContext]);
    // TODO - there are way too many styles/theme providers to be needed here
    const partialApp = React.useMemo(() => (React.createElement(Router, null,
        React.createElement(NavigateToProvider, null),
        React.createElement(LocalStorageStatesSynchronizer, null),
        React.createElement(InnoWeaveThemeConnector, { hasWeaveTheme: hasWeaveTheme },
            React.createElement(InnoStylesProvider, { theme: theme, hasWeaveTheme: hasWeaveTheme },
                React.createElement(SiteConfigProvider, { url: configUrl, config: config },
                    React.createElement(ModalsForUserHelp, { helpModals: helpModals }),
                    React.createElement(LogoutContextProvider, null,
                        React.createElement(AuthenticationRouter, { hasLoginPicker: loginPicker, authenticate: authenticate, hasSharedWorkers: hasSharedWorkers },
                            React.createElement(FeatureFlagProvider, null,
                                React.createElement(AboutToExpireFeature, { hasSharedWorkers: hasSharedWorkers }),
                                React.createElement(SettingsProvider, null,
                                    React.createElement(AnalyticsProviderWrapper, null,
                                        React.createElement(LoadingProvider, null,
                                            React.createElement(MapProvider, { disable: disableMapProvider },
                                                React.createElement(S3DataAuthProvider, null,
                                                    React.createElement(Notifier, { maxSnack: 3 },
                                                        React.createElement(LocalizationProviderLuxon, null,
                                                            React.createElement(NotificationHubProvider, { notificationHub: notificationHub },
                                                                React.createElement(GlobalStyle, null),
                                                                React.createElement(ErrorBoundary, null,
                                                                    React.createElement(App, { id: "inno-app" },
                                                                        withHelpbot ? (React.createElement(MoveableChatbot, null)) : null,
                                                                        React.createElement(ChooseHeader, { hasLoginPicker: !!loginPicker, branding: branding, isTenantAware: isTenantAware, helpUrl: helpUrl, aboutBox: aboutBox, inEmulation: inEmulation, notificationConfig: notificationConfig, withHelpBot: withHelpbot }),
                                                                        React.createElement(StyledAppBody, { id: "inno-app-body" },
                                                                            React.createElement(TermsAndConditionsWrapper, { validateTerms: validateTerms },
                                                                                React.createElement(ExpiredTenantDialogWrapper, { expiredSubscriptionsSet: expiredSubscriptionsSet },
                                                                                    React.createElement(Pages, { pages: authorizedPages, showSideNav: showSideNav })))))))))))))))))))))), [
        generateClassName,
        theme,
        configUrl,
        config,
        loginPicker,
        authenticate,
        branding,
        isTenantAware,
        helpUrl,
        aboutBox,
        authorizedPages,
        showSideNav,
    ]);
    return globalization ? (React.createElement(GlobalizationProvider, { ...globalization }, partialApp)) : (partialApp);
};
ApplicationWrapper.defaultProps = {
    config: null,
    authenticate: true,
};
export default React.memo(ApplicationWrapper);
